import { useEffect, useRef, useState } from 'react';

/*
  Diploma de FHC Academy. Se dibuja a tamaño A4 apaisado (1123 × 794 px = 297 × 210 mm a 96 ppp)
  y se escala al ancho disponible en pantalla. Al imprimir (o «Guardar como PDF») ocupa la hoja entera.
  Texto acordado con Samuel (08/10/2026): «[NOMBRE COMPLETO] ha completado exitosamente la formación de
  FHC Academy de tokenización y mercado inmobiliario».
  Firma (02/11/2026): solo Luna Trejos Grajales, Dirección de Formación. A la izquierda, la fecha de expedición.
*/

const W = 1123;
const H = 794;
const GOLD = '#c9a84c';
const GOLD_LT = '#e3c87a';

export function formatIssued(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function Corner({ style }) {
  return (
    <svg width="74" height="74" viewBox="0 0 74 74" fill="none" style={{ position: 'absolute', ...style }} aria-hidden="true">
      <path d="M2 72V18C2 9.2 9.2 2 18 2h54" stroke={GOLD} strokeWidth="1.5" />
      <path d="M12 72V26c0-7.7 6.3-14 14-14h46" stroke={GOLD} strokeWidth="0.75" opacity="0.6" />
      <rect x="20" y="20" width="9" height="9" transform="rotate(45 24.5 24.5)" fill={GOLD} />
    </svg>
  );
}

export function DiplomaSheet({ cert, verifyUrl }) {
  const sentence = cert.certificate_title || 'la formación de FHC Academy de tokenización y mercado inmobiliario';
  const nameSize = cert.full_name && cert.full_name.length > 34 ? 46 : 58;
  return (
    <div className="diploma-sheet" data-testid="diploma-sheet" style={{
      width: W, height: H, position: 'relative', overflow: 'hidden', color: '#fff',
      background: 'radial-gradient(ellipse at 50% 38%, #13284a 0%, #0a1628 55%, #060d1b 100%)',
      fontFamily: "'Manrope', sans-serif", WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
    }}>
      {/* Marco doble */}
      <div style={{ position: 'absolute', inset: 22, border: `1.5px solid ${GOLD}` }} />
      <div style={{ position: 'absolute', inset: 32, border: `0.75px solid ${GOLD}`, opacity: 0.55 }} />
      <Corner style={{ top: 14, left: 14 }} />
      <Corner style={{ top: 14, right: 14, transform: 'scaleX(-1)' }} />
      <Corner style={{ bottom: 14, left: 14, transform: 'scaleY(-1)' }} />
      <Corner style={{ bottom: 14, right: 14, transform: 'scale(-1,-1)' }} />
      {/* Marca de agua */}
      <img src="/logo-fhc.png" alt="" aria-hidden="true" style={{
        position: 'absolute', left: '50%', top: '52%', width: 520, transform: 'translate(-50%,-50%)', opacity: 0.03, filter: 'grayscale(1)',
      }} />

      <div style={{ position: 'absolute', inset: '58px 96px 52px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <img src="/logo-fhc.png" alt="Freedom Homes Club" style={{ height: 100, width: 'auto' }} />
        <p style={{ marginTop: 14, fontFamily: "'Cinzel', serif", letterSpacing: '0.42em', fontSize: 15, color: GOLD }}>FHC ACADEMY</p>
        <h1 style={{
          marginTop: 6, fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: 50, letterSpacing: '0.02em', lineHeight: 1.1,
          color: GOLD_LT, textShadow: '0 1px 0 rgba(0,0,0,0.35)',
        }}>Diploma</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <span style={{ width: 60, height: 1, background: GOLD, opacity: 0.6 }} />
          <span style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
          <span style={{ width: 60, height: 1, background: GOLD, opacity: 0.6 }} />
        </div>

        <p data-testid="diploma-name" style={{
          marginTop: 30, fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: nameSize, lineHeight: 1.15, color: '#fff',
          maxWidth: 900,
        }}>{cert.full_name}</p>
        <div style={{ width: 520, height: 1, marginTop: 14, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p data-testid="diploma-text" style={{ marginTop: 20, fontSize: 21, lineHeight: 1.55, color: '#e7e2d6', maxWidth: 760, fontFamily: "'Playfair Display', serif" }}>
          ha completado exitosamente {sentence}
        </p>

        <div style={{ marginTop: 'auto', width: '100%', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'end', gap: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: '#fff' }}>{formatIssued(cert.issued_at)}</p>
            <div style={{ height: 1, background: GOLD, opacity: 0.7, margin: '8px 40px' }} />
            <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD }}>Fecha de expedición</p>
          </div>
          <div style={{ textAlign: 'center', padding: '0 12px' }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%', border: `1.5px solid ${GOLD}`, margin: '0 auto', display: 'flex', alignItems: 'center',
              justifyContent: 'center', boxShadow: `inset 0 0 0 5px #0a1628, inset 0 0 0 6px ${GOLD}`,
            }}>
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: 13, letterSpacing: '0.12em', color: GOLD, lineHeight: 1.25 }}>FHC<br />ACADEMY</span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: '#fff' }}>Luna Trejos Grajales</p>
            <div style={{ height: 1, background: GOLD, opacity: 0.7, margin: '8px 40px' }} />
            <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD }}>Dirección de Formación · FHC Academy</p>
          </div>
        </div>

        <p style={{ marginTop: 22, fontSize: 10.5, color: '#9aa3b5', lineHeight: 1.6 }}>
          Código de verificación <span data-testid="diploma-code" style={{ color: GOLD, fontWeight: 700, letterSpacing: '0.08em' }}>{cert.code}</span>
          {verifyUrl ? <> · {verifyUrl}</> : null}
          <br />
          Acreditación privada e interna de Freedom Homes Club. No tiene equivalencia académica oficial ni habilita por sí sola para comercializar.
        </p>
      </div>
    </div>
  );
}

/* Escala el diploma al ancho del contenedor en pantalla; en impresión va a tamaño real. */
export default function Diploma({ cert, verifyUrl }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, el.clientWidth / W));
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    window.addEventListener('resize', fit);
    return () => { ro?.disconnect(); window.removeEventListener('resize', fit); };
  }, []);
  return (
    <div ref={ref} className="diploma-frame w-full" style={{ height: H * scale }}>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: #0a1628 !important; }
          body * { visibility: hidden !important; }
          .diploma-print, .diploma-print * { visibility: visible !important; }
          .diploma-print { position: fixed !important; left: 0; top: 0; width: 297mm; height: 210mm; margin: 0; }
          .diploma-print .diploma-scale { transform: none !important; }
          .diploma-frame { height: auto !important; }
        }
      `}</style>
      <div className="diploma-print" style={{ width: W * scale }}>
        <div className="diploma-scale shadow-2xl shadow-black/50" style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <DiplomaSheet cert={cert} verifyUrl={verifyUrl} />
        </div>
      </div>
    </div>
  );
}
